import React from "react";

// Deterministic pseudo-random so a template's scattered shapes look the same
// on every render and every device.
const rand = (seed) => {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
};

function Sprig({ className }) {
  return (
    <svg className={`inv__orn ${className}`} viewBox="0 0 120 120" aria-hidden="true">
      <path
        d="M4 6c30 6 62 30 78 74M22 12c10 14 12 30 8 44M48 30c16 2 30 10 40 22"
        fill="none"
        stroke="var(--inv-accent)"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <g fill="var(--inv-accent)" opacity="0.85">
        <ellipse cx="22" cy="34" rx="6" ry="13" transform="rotate(-18 22 34)" />
        <ellipse cx="40" cy="22" rx="6" ry="13" transform="rotate(-62 40 22)" />
        <ellipse cx="58" cy="52" rx="6" ry="14" transform="rotate(-28 58 52)" />
        <ellipse cx="72" cy="36" rx="5.5" ry="12" transform="rotate(-70 72 36)" />
        <ellipse cx="78" cy="72" rx="5" ry="11" transform="rotate(-12 78 72)" />
      </g>
      <g fill="var(--inv-accent2)">
        <circle cx="14" cy="60" r="6" />
        <circle cx="62" cy="14" r="5" />
        <circle cx="94" cy="56" r="4.5" />
        <circle cx="30" cy="54" r="3" />
      </g>
    </svg>
  );
}

function Balloons() {
  return (
    <svg className="inv__orn inv__orn--balloons" viewBox="0 0 140 190" aria-hidden="true">
      <g fill="none" stroke="var(--inv-ink)" strokeWidth="1" opacity="0.35">
        <path d="M40 78q6 40-4 110M82 62q-8 60 0 128M112 92q-10 40 4 96" />
      </g>
      <ellipse cx="40" cy="48" rx="24" ry="30" fill="var(--inv-accent)" />
      <ellipse cx="82" cy="34" rx="24" ry="30" fill="var(--inv-accent2)" />
      <ellipse cx="112" cy="64" rx="22" ry="28" fill="var(--inv-accent)" opacity="0.6" />
      <g fill="#fff" opacity="0.45">
        <ellipse cx="32" cy="36" rx="5" ry="9" transform="rotate(20 32 36)" />
        <ellipse cx="74" cy="22" rx="5" ry="9" transform="rotate(20 74 22)" />
      </g>
    </svg>
  );
}

function Stars() {
  const stars = Array.from({ length: 26 }, (_, index) => {
    const x = rand(index + 11) * 500;
    const y = rand(index + 31) * 700;
    const size = 3 + rand(index + 51) * 9;
    const color = index % 2 ? "var(--inv-accent)" : "var(--inv-accent2)";
    return (
      <path
        key={index}
        d={`M${x} ${y - size}Q${x} ${y} ${x + size} ${y}Q${x} ${y} ${x} ${y + size}Q${x} ${y} ${x - size} ${y}Q${x} ${y} ${x} ${y - size}Z`}
        fill={color}
        opacity={0.35 + rand(index + 71) * 0.65}
      />
    );
  });
  return (
    <svg className="inv__orn inv__orn--fill" viewBox="0 0 500 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {stars}
    </svg>
  );
}

function Clouds() {
  const cloud = (x, y, scale, opacity) => (
    <g transform={`translate(${x} ${y}) scale(${scale})`} fill="#fff" opacity={opacity}>
      <ellipse cx="50" cy="40" rx="50" ry="22" />
      <circle cx="32" cy="28" r="20" />
      <circle cx="62" cy="22" r="26" />
    </g>
  );
  return (
    <svg className="inv__orn inv__orn--top" viewBox="0 0 500 150" preserveAspectRatio="xMidYMin slice" aria-hidden="true">
      {cloud(-10, 20, 1.3, 0.95)}
      {cloud(190, 0, 0.9, 0.7)}
      {cloud(350, 34, 1.4, 0.95)}
      <circle cx="255" cy="96" r="5" fill="var(--inv-accent2)" />
      <circle cx="120" cy="120" r="4" fill="var(--inv-accent2)" />
      <circle cx="330" cy="18" r="4" fill="var(--inv-accent2)" />
    </svg>
  );
}

function Geo() {
  return (
    <>
      <svg className="inv__orn inv__orn--tr" viewBox="0 0 120 120" aria-hidden="true">
        <circle cx="92" cy="28" r="62" fill="var(--inv-accent)" />
        <circle cx="26" cy="70" r="12" fill="var(--inv-accent2)" />
      </svg>
      <svg className="inv__orn inv__orn--bl" viewBox="0 0 120 120" aria-hidden="true">
        <path d="M0 120V40a80 80 0 0 1 80 80z" fill="var(--inv-accent2)" />
        <g fill="var(--inv-accent)">
          {Array.from({ length: 9 }, (_, index) => (
            <circle key={index} cx={78 + (index % 3) * 12} cy={36 + Math.floor(index / 3) * 12} r="2.4" />
          ))}
        </g>
      </svg>
    </>
  );
}

// A curved branch of paired leaves (used in opposite corners).
function Branch({ className }) {
  const leaves = Array.from({ length: 7 }, (_, index) => {
    const along = 14 + index * 13;
    const x = along;
    const y = 8 + (along * along) / 150;
    const tilt = 28 + index * 6;
    return (
      <g key={index} transform={`translate(${x} ${y}) rotate(${tilt})`}>
        <ellipse cx="0" cy="-9" rx="4" ry="9" />
        <ellipse cx="0" cy="9" rx="4" ry="9" />
      </g>
    );
  });
  return (
    <svg className={`inv__orn ${className}`} viewBox="0 0 120 120" aria-hidden="true">
      <path d="M6 6Q50 14 108 96" fill="none" stroke="var(--inv-accent)" strokeWidth="1.4" strokeLinecap="round" />
      <g fill="var(--inv-accent)" opacity="0.8">
        {leaves}
      </g>
      <circle cx="108" cy="98" r="4" fill="var(--inv-accent2)" />
    </svg>
  );
}

const HEART = "M0 6C-10 -4 -6 -12 0 -6C6 -12 10 -4 0 6Z";

function Hearts() {
  const hearts = Array.from({ length: 16 }, (_, index) => {
    const x = rand(index + 211) * 500;
    const y = rand(index + 233) * 700;
    const scale = 1 + rand(index + 251) * 2.4;
    const tilt = (rand(index + 271) - 0.5) * 50;
    return (
      <path
        key={index}
        d={HEART}
        transform={`translate(${x.toFixed(0)} ${y.toFixed(0)}) rotate(${tilt.toFixed(0)}) scale(${scale.toFixed(2)})`}
        fill={index % 2 ? "var(--inv-accent)" : "var(--inv-accent2)"}
        opacity={0.25 + rand(index + 291) * 0.45}
      />
    );
  });
  return (
    <svg className="inv__orn inv__orn--fill" viewBox="0 0 500 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {hearts}
    </svg>
  );
}

function Waves() {
  return (
    <svg className="inv__orn inv__orn--bottom" viewBox="0 0 500 120" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 50Q62 10 125 50T250 50T375 50T500 50V120H0Z" fill="var(--inv-accent2)" opacity="0.55" />
      <path d="M0 72Q62 38 125 72T250 72T375 72T500 72V120H0Z" fill="var(--inv-accent)" opacity="0.6" />
      <path d="M0 94Q62 66 125 94T250 94T375 94T500 94V120H0Z" fill="var(--inv-accent)" />
    </svg>
  );
}

function Sunburst() {
  const rays = Array.from({ length: 18 }, (_, index) => {
    const angle = (index * 180) / 17;
    return (
      <path
        key={index}
        d="M250 0L262 520L238 520Z"
        transform={`rotate(${(angle - 90).toFixed(1)} 250 0)`}
        fill={index % 2 ? "var(--inv-accent)" : "var(--inv-accent2)"}
      />
    );
  });
  return (
    <svg className="inv__orn inv__orn--fill inv__orn--rays" viewBox="0 0 500 700" preserveAspectRatio="xMidYMin slice" aria-hidden="true">
      {rays}
      <circle cx="250" cy="0" r="46" fill="var(--inv-accent)" />
    </svg>
  );
}

function Bunting() {
  const flags = Array.from({ length: 9 }, (_, index) => {
    const x = 28 + index * 55.5;
    const y = 18 + Math.sin((index / 8) * Math.PI) * 34;
    const fills = ["var(--inv-accent)", "var(--inv-accent2)", "var(--inv-ink)"];
    return <path key={index} d={`M${x - 20} ${y}h40l-20 44z`} fill={fills[index % 3]} />;
  });
  return (
    <svg className="inv__orn inv__orn--top" viewBox="0 0 500 120" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 10Q250 86 500 10" fill="none" stroke="var(--inv-ink)" strokeWidth="2" />
      {flags}
    </svg>
  );
}

function Grid() {
  return (
    <svg className="inv__orn inv__orn--fill inv__orn--grid" viewBox="0 0 500 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g stroke="var(--inv-accent)" strokeWidth="1" fill="none">
        {Array.from({ length: 11 }, (_, index) => (
          <path key={`v${index}`} d={`M${index * 50} 0V700`} />
        ))}
        {Array.from({ length: 15 }, (_, index) => (
          <path key={`h${index}`} d={`M0 ${index * 50}H500`} />
        ))}
      </g>
      <g stroke="var(--inv-accent2)" strokeWidth="2.5" fill="none">
        <circle cx="400" cy="100" r="34" />
        <path d="M350 100h100M400 50v100" />
      </g>
    </svg>
  );
}

function Stripes() {
  return (
    <svg className="inv__orn inv__orn--tr" viewBox="0 0 120 120" aria-hidden="true">
      {Array.from({ length: 6 }, (_, index) => (
        <path
          key={index}
          d={`M${index * 20} 0h10L120 ${120 - index * 20 - 10}v10z`}
          fill={index % 2 ? "var(--inv-accent2)" : "var(--inv-accent)"}
        />
      ))}
    </svg>
  );
}

export default function Ornament({ kind }) {
  switch (kind) {
    case "floral":
      return (
        <>
          <Sprig className="inv__orn--tl" />
          <Sprig className="inv__orn--br inv__orn--flip" />
        </>
      );
    case "balloons":
      return <Balloons />;
    case "stars":
      return <Stars />;
    case "clouds":
      return <Clouds />;
    case "geo":
      return <Geo />;
    case "deco":
      return (
        <>
          <span className="inv__frame" />
          <span className="inv__frame inv__frame--inner" />
        </>
      );
    case "arch":
      return <span className="inv__arch" />;
    case "laurel":
      return (
        <>
          <Branch className="inv__orn--tl" />
          <Branch className="inv__orn--br inv__orn--flip" />
        </>
      );
    case "hearts":
      return <Hearts />;
    case "waves":
      return <Waves />;
    case "sunburst":
      return <Sunburst />;
    case "bunting":
      return <Bunting />;
    case "grid":
      return <Grid />;
    case "stripes":
      return <Stripes />;
    default:
      return null;
  }
}
